import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore, useItemsStore, useSessionStore, useSuppliersStore, useTradeItemsStore } from "@/stores";
import type ISupplierItem from "@/models/responses/ISupplierItem";
import SyncSupplierItemsRequest from "@/models/requests/SyncSupplierItemsRequest";
import SupplierItem from "@/models/frontend/SupplierItem";
import SupplierWithItems from "@/models/frontend/SupplierWithItems";

export const useSupplierItemsStore = defineStore("supplierItems", {
  state: () => ({
    error: "" as any,
    data: [] as SupplierItem[],
    loadedTenantId: 0,
  }),
  actions: {
    resetLoaded() {
      this.loadedTenantId = 0;
    },
    async fetchAll(force = false) {
      const authStore = useAuthStore();
      const sessionStore = useSessionStore();

      const { isAuthenticated } = storeToRefs(authStore);
      const { selectedTenantId } = storeToRefs(sessionStore);

      if (!isAuthenticated.value) return;
      if (!force && this.loadedTenantId === selectedTenantId.value) return;

      this.error = "";
      await api
        .get<ISupplierItem[]>(`v1.0/supplier-items?tenantId=${selectedTenantId.value}`)
        .then((response) => {
          this.data = response.data.map((supplierItem) => this.mapToFrontend(supplierItem));
          this.loadedTenantId = selectedTenantId.value;
        })
        .catch((error) => (this.error = error));
    },
    async sync(req: SyncSupplierItemsRequest, onfulfilled: (data: ISupplierItem[]) => void, onrejected: (reason: any) => void | PromiseLike<void>) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .post<ISupplierItem[]>(`v1.0/supplier-items`, req)
        .then((response) => {
          const mappedResponse = response.data.map((supplierItem) => this.mapToFrontend(supplierItem));
          // remove items that are equal to the requested supplierId, because the response contains all items for the supplier
          this.data = this.data.filter((x) => x.supplier.id !== req.supplierId);
          mappedResponse.forEach((item) => {
            const index = this.data.findIndex((x) => x.id === item.id);
            if (index === -1) this.data.push(item);
            else this.data[index] = item;
          });
          onfulfilled(response.data);
        })
        .catch((error) => {
          this.error = error;
          onrejected(error);
        });
    },
    mapToFrontend(supplierItem: ISupplierItem): SupplierItem {
      const itemsStore = useItemsStore();
      const tradeItemsStore = useTradeItemsStore();
      const supplier = useSuppliersStore().getById(supplierItem.supplierId);
      const item = itemsStore.getById(supplierItem.itemId);
      const tradeItem = tradeItemsStore.getByItemIdAndUnitCode(supplierItem.itemId, supplierItem.orderUnitCode);
      if (tradeItem === undefined) {
        return new SupplierItem(supplierItem.id, supplier, item, supplierItem.code, supplierItem.orderUnitCode, 1, supplierItem.orderUnitCode, supplierItem.name, supplierItem.description, supplierItem.proposedOrderQuantity);
      }
      else {
        return new SupplierItem(supplierItem.id, supplier, item, supplierItem.code, supplierItem.orderUnitCode, tradeItem.factor, item.baseUnitCode, supplierItem.name, supplierItem.description, supplierItem.proposedOrderQuantity);
      }
    },
    setSupplierItems(supplierWithItems: SupplierWithItems) {
      this.data = this.data.filter((x) => x.supplier.id !== supplierWithItems.id);
      this.data.push(...supplierWithItems.supplierItems);
    },
  },
  getters: {
    getById: (state) => (id: number) => state.data.find((supplierItem) => supplierItem.id === id),
    getByItemId: (state) => (itemId: number) => state.data.find((supplierItem) => supplierItem.item.id === itemId),
    getBySupplierAndCode: (state) => (supplierId: number, code: string) => state.data.find((supplierItem) => supplierItem.supplier.id === supplierId && supplierItem.code === code),
    getSupplierItems: (state) => (supplierId: number) => state.data.filter((supplierItem) => supplierItem.supplier.id === supplierId),
  },
});
